import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, renderList as _renderList, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "d-flex flex-column flex-md-row gap-4" }
const _hoisted_2 = { class: "flex-shrink-0 d-none d-md-block ifa-selector-desktop" }
const _hoisted_3 = ["title", "onClick"]
const _hoisted_4 = {
  key: 0,
  class: "d-md-none"
}
const _hoisted_5 = { class: "flex-grow-1 min-width-0" }
const _hoisted_6 = {
  key: 0,
  class: "border rounded overflow-hidden"
}
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = { class: "p-2 p-md-4" }
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = { class: "fw-bold" }
const _hoisted_11 = { class: "mt-4 fst-italic" }
const _hoisted_12 = { class: "list-unstyled ms-3" }
const _hoisted_13 = { class: "list-unstyled ms-3 mb-0" }
const _hoisted_14 = { class: "ifa-meta mb-3" }
const _hoisted_15 = { class: "ifa-meta-row" }
const _hoisted_16 = { class: "ifa-meta-row" }
const _hoisted_17 = ["href"]
const _hoisted_18 = { class: "mb-0 small-text" }
const _hoisted_19 = { class: "small-text" }
const _hoisted_20 = { class: "fst-italic" }

import { onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { ITL1_REGIONS } from '@/guiide/ts/geo/itl';
import { IFA, IFAs } from '@/guiide/ts/ifa/ifa';
import { presentValueOfPensionsAndSavings } from '@/guiide/ts/utilities';
import { formatter } from '@/shared/ts/formatter';
import { app } from '@/guiide/ts/app';
import Dropdown from '../Dropdown.vue';
import ObjectDropdown from '../ObjectDropdown.vue';
import AdvisorsList from '../AdvisorsList.vue';
import TextInput from '../TextInput.vue';
import LockButton from '../LockButton.vue';
import DynamicAdviceViewer from '../DynamicAdviceViewer.vue';

const lowAssetValueThreshold = 250000;
const selectedRegionDefault = "Please select your region";

export default /*@__PURE__*/_defineComponent({
  __name: 'IFAList',
  setup(__props) {

const totalAssetValueAtPresent = computed<number>(() => presentValueOfPensionsAndSavings());
const showLowAssetsOffering = computed<boolean>(() => totalAssetValueAtPresent.value < lowAssetValueThreshold);

const regions = [ selectedRegionDefault, ...ITL1_REGIONS.map(r => r.name) ];
const selectedRegion = ref<string>(selectedRegionDefault);
const qualifyingIFAs = ref<Array<IFA>>(new Array<IFA>());
const selectedIFA = ref<IFA|null>();
const imageCache = new Map<string, string>();

const requestorNameInput = ref(null);
const requestorName = ref("");
const phoneNumber = ref("");

const showSendPlanInputArea = ref(true);
const responseMessage = ref("");
const responseOutcome = ref("success");

onMounted(async () => {
    await loadImagesIntoCache();
})

watch(
    () => selectedRegion.value,
    () => {
        if (selectedRegion.value == "") {
            qualifyingIFAs.value = new Array<IFA>();
            selectedIFA.value = null;
            return;
        }

        qualifyingIFAs.value = IFAs
            .filter(ifa => ifa.itl1Region.some(region => region.name == selectedRegion.value))
            .sort((a,b) => a.abbrevName.localeCompare(b.abbrevName));
        selectedIFA.value = qualifyingIFAs.value.length > 0 ? qualifyingIFAs.value[0] : null;

        resetSuccessReceipt();
    },
    { immediate: true }
);

const sendMyPlanButtonText = computed<string>(() => `Send my plan to ${selectedIFA.value.abbrevName}`);

function selectIFA(ifa: IFA) {
    selectedIFA.value = ifa;
    resetSuccessReceipt();
}

async function onSendMyPlan() {
    responseMessage.value = "";

    if (!requestorNameInput.value.isValid()) {
        responseMessage.value = "Please tell us your name.";
        responseOutcome.value = "warning";
        return;
    }

    const result = await app.requestIFAContact(selectedIFA.value.abbrevName, requestorName.value, phoneNumber.value);

    showSendPlanInputArea.value = !result.successful;
    responseMessage.value = result.successful
        ? `Thank you for your request. We will contact ${selectedIFA.value.abbrevName} shortly.`
        : result.message;
    responseOutcome.value = result.successful ? "success" : "warning";

    if (result.successful) {
        requestorName.value = "";
        phoneNumber.value = "";
    }
}

function resetSuccessReceipt() {
    responseMessage.value = "";
    showSendPlanInputArea.value = true;
}

async function loadImagesIntoCache() {
    const images = IFAs.flatMap(ifa => [
        ifa.logo,
        ...(ifa.hero ? [ ifa.hero ] : [])
    ]);

    await Promise.all(
        images.map(image => loadImageIntoCache(image))
    );
}

async function loadImageIntoCache(image: string) {
    const src = getImageSrc(image);
    const response = await fetch(src);
    const blob = await response.blob();
    
    const objectUrl = URL.createObjectURL(blob);

    const preloadImage = new Image();
    preloadImage.src = objectUrl;
    await preloadImage.decode();
    
    imageCache.set(image, objectUrl);
}

function getCachedImageSrc(logoName: string): string | undefined {
  return imageCache.get(logoName);
}

const getImageSrc = (logoName: string): string => "content/advisors/" + logoName;

onUnmounted(() => {
    imageCache.forEach(src => URL.revokeObjectURL(src));
    imageCache.clear();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _cache[16] || (_cache[16] = _createElementVNode("h3", { class: "mt-5 mb-4" }, "Retirement Advisers", -1)),
    _cache[17] || (_cache[17] = _createElementVNode("p", null, "Guiide can't give you financial advice. We can't give recommendations on where to invest your pension pots, what products are best for you, or how to best deal with more complicated tax issues.", -1)),
    _cache[18] || (_cache[18] = _createElementVNode("p", null, " If you want advice, you can take this solely at retirement, or on an ongoing basis, where the adviser reviews everything each year with you and may make new recommendations. One off retirement advice may cost around 2 - 3% of your pensions and savings pots, or a fixed agreed cost. Ongoing advice is usually between 0.5% - 1% of your pots each year. ", -1)),
    _cache[19] || (_cache[19] = _createElementVNode("p", null, "We have a number of advisers we work with and have checked their advice permissions, costs and processes appear suitable.", -1)),
    _cache[20] || (_cache[20] = _createElementVNode("p", null, "To help with costs the Government also allows people in many pension schemes to withdraw up to £500 tax free from their pension pot to pay towards retirement advice, if your pension provider allows this.", -1)),
    (false || showLowAssetsOffering.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _cache[4] || (_cache[4] = _createElementVNode("p", null, " As your total pension and savings pots are below £250,000 it may be harder finding advice as many have minimums above this level. However LV= can provide advice for people below this level. ", -1)),
          _createVNode(AdvisorsList, {
            "always-display": true,
            "adviser-keys-filter": ['LV']
          })
        ], 64))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("p", null, " As your total pension and savings pots are above " + _toDisplayString(_unref(formatter).formatCurrency(lowAssetValueThreshold)) + " finding advice, if wanted, should be straightforward. Most people want an adviser near them to make meetings etc easier if needed. We work with a range of advisers in all parts of the UK. ", 1),
          _createElementVNode("div", null, [
            _createVNode(Dropdown, {
              items: regions,
              modelValue: selectedRegion.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedRegion).value = $event)),
              "show-label": false,
              classes: "form-select form-control mb-3 mb-md-5"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(qualifyingIFAs.value, (ifa) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: ifa.registrationNumber
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["list-group-item list-group-item-action ifa-list-item text-start text-truncate mb-3 px-0", { 'active': ifa === selectedIFA.value }]),
                    title: ifa.name,
                    onClick: ($event: any) => (selectIFA(ifa))
                  }, _toDisplayString(ifa.abbrevName), 11, _hoisted_3),
                  _cache[5] || (_cache[5] = _createElementVNode("hr", null, null, -1))
                ], 64))
              }), 128))
            ]),
            (qualifyingIFAs.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(ObjectDropdown, {
                    items: qualifyingIFAs.value,
                    modelValue: selectedIFA.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedIFA).value = $event)),
                    "get-display-text": ifa => ifa.abbrevName,
                    "get-key": ifa => ifa.registrationNumber,
                    class: "form-select form-control mb-0"
                  }, null, 8, ["items", "modelValue", "get-display-text", "get-key"])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_5, [
              (selectedIFA.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (selectedIFA.value.hero)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: selectedIFA.value.abbrevName,
                          class: _normalizeClass(["ifa-hero", { 'ifa-hero-framed': selectedIFA.value.heroFramed }]),
                          style: _normalizeStyle(selectedIFA.value.heroContainerStyle)
                        }, [
                          _createElementVNode("img", {
                            src: getImageSrc(selectedIFA.value.hero),
                            style: _normalizeStyle(selectedIFA.value.heroStyle),
                            alt: `${selectedIFA.value.abbrevName} banner`
                          }, null, 12, _hoisted_7)
                        ], 6))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_8, [
                      (_openBlock(), _createElementBlock("div", {
                        class: "ifa-logo mb-4",
                        style: _normalizeStyle(selectedIFA.value.logoContainerStyle),
                        key: selectedIFA.value.abbrevName
                      }, [
                        _createElementVNode("img", {
                          src: getCachedImageSrc(selectedIFA.value.logo),
                          class: _normalizeClass(["border rounded", selectedIFA.value.logoClass]),
                          alt: selectedIFA.value.abbrevName
                        }, null, 10, _hoisted_9)
                      ], 4)),
                      _createElementVNode("h4", _hoisted_10, _toDisplayString(selectedIFA.value.name), 1),
                      _cache[12] || (_cache[12] = _createElementVNode("hr", null, null, -1)),
                      _createElementVNode("p", _hoisted_11, "\"" + _toDisplayString(selectedIFA.value.description) + "\"", 1),
                      _createElementVNode("section", null, [
                        _cache[6] || (_cache[6] = _createElementVNode("p", { class: "fst-italic mb-1" }, "Initial fees", -1)),
                        _createElementVNode("ul", _hoisted_12, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedIFA.value.initialFeesDescription, (fee, index) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: `initial-${index}`
                            }, _toDisplayString(fee), 1))
                          }), 128))
                        ]),
                        _cache[7] || (_cache[7] = _createElementVNode("p", { class: "fst-italic mb-1" }, "Ongoing fees", -1)),
                        _createElementVNode("ul", _hoisted_13, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedIFA.value.ongoingFeesDescription, (fee, index) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: `ongoing-${index}`
                            }, _toDisplayString(fee), 1))
                          }), 128))
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "ifa-meta-label" }, "FCA number:", -1)),
                          _createElementVNode("div", null, _toDisplayString(selectedIFA.value.registrationNumber), 1)
                        ]),
                        _createElementVNode("div", _hoisted_16, [
                          _cache[10] || (_cache[10] = _createElementVNode("div", { class: "ifa-meta-label" }, "Website:", -1)),
                          _createElementVNode("div", null, [
                            _createElementVNode("a", {
                              href: selectedIFA.value.website,
                              class: "ifa-website",
                              target: "_blank"
                            }, _toDisplayString(selectedIFA.value.website), 9, _hoisted_17),
                            _createElementVNode("p", _hoisted_18, [
                              _createTextVNode(" If you go on to take advice from " + _toDisplayString(selectedIFA.value.abbrevName) + " after being introduced through the '", 1),
                              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "fst-italic" }, "Send my plan", -1)),
                              _createTextVNode("' option below, " + _toDisplayString(selectedIFA.value.abbrevName) + " will pay Guiide a one-off referral fee. This won't increase the cost of the advice to you. ", 1)
                            ])
                          ])
                        ])
                      ]),
                      _cache[13] || (_cache[13] = _createElementVNode("hr", { class: "my-4" }, null, -1)),
                      _cache[14] || (_cache[14] = _createElementVNode("p", { class: "fw-bold" }, "Can Guiide help?", -1)),
                      _createElementVNode("p", null, " If you would like us to, we can provide your Guiide plan to " + _toDisplayString(selectedIFA.value.abbrevName) + ". If they are able to help, they will contact you directly. Otherwise, we will let you know. We will use the email address registered with Guiide to contact you, unless you'd prefer to provide a phone number below. ", 1),
                      _cache[15] || (_cache[15] = _createElementVNode("p", null, " If they think they can help, they will provide a free, no obligation initial discussion. You can then decide if advice from them is right for you. If you do take advice, we will follow up with you post advice to understand if you were happy with the advice process provided. ", -1)),
                      (showSendPlanInputArea.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createVNode(TextInput, {
                              ref_key: "requestorNameInput",
                              ref: requestorNameInput,
                              modelValue: requestorName.value,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((requestorName).value = $event)),
                              optional: false,
                              description: "Your name",
                              maxlength: "25",
                              class: "mb-2"
                            }, null, 8, ["modelValue"]),
                            _createVNode(TextInput, {
                              modelValue: phoneNumber.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((phoneNumber).value = $event)),
                              optional: true,
                              description: "Your phone number",
                              maxlength: "20",
                              class: "mb-3"
                            }, null, 8, ["modelValue"]),
                            _createElementVNode("p", _hoisted_19, [
                              _cache[11] || (_cache[11] = _createTextVNode(" By using the ")),
                              _createElementVNode("span", _hoisted_20, "'" + _toDisplayString(sendMyPlanButtonText.value) + "'", 1),
                              _createTextVNode(" button, you are consenting to Guiide sharing your Plan report with " + _toDisplayString(selectedIFA.value.abbrevName) + " to consider whether they can provide you with advice. There is no obligation to take advice or pay any costs to them. Only choose advice with them if you feel they are the right adviser to meet your retirement planning needs after any initial discussion. ", 1)
                            ]),
                            _createVNode(LockButton, {
                              text: sendMyPlanButtonText.value,
                              "async-action": onSendMyPlan
                            }, null, 8, ["text"])
                          ], 64))
                        : _createCommentVNode("", true),
                      _createVNode(DynamicAdviceViewer, {
                        message: responseMessage.value,
                        "alert-context": responseOutcome.value
                      }, null, 8, ["message", "alert-context"])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ], 64))
  ], 64))
}
}

})